import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Menu from "../../components/Menu";
import Form from "../../../../../components/shared/Form";
import Table from "../../components/Table";
import Filters from "../components/Filters";
import Settings from "./Settings";
import { useColumns, useHiddenColumns } from "./hooks/useColumns";
import {
    useColumns as useTimeSheetColumns,
    useHiddenColumns as useTimeSheetHiddenColumns,
} from "../hooks/useTimesheetColumns";
import { useFilters } from "./hooks/useFilters";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";
import { buildColumnsValue, buildFiltersValue } from "../helpers";
import type { TeamTimesheetFormValues } from "./types";

const options: string[] = [
    "user-columns",
    "user-filters",
    "timesheet-columns",
    "settings",
];

function Team() {
    const [option, setOption] = useState<string>("user-columns");
    const columns = useColumns();
    const hiddenColumns = useHiddenColumns();
    const timeSheetColumns = useTimeSheetColumns();
    const timeSheetHiddenColumns = useTimeSheetHiddenColumns();
    const filters = useFilters();

    const config = useCompanyPreferences("timeSheetTeam");
    const { mutate, isPending } = usePreferenceMutation("company", "timeSheetTeam");

    const defaultColumnsValue = useMemo(() => {
        return buildColumnsValue(columns, hiddenColumns, config?.columns);
    }, [columns, hiddenColumns, config]);

    const defaultTimesheetColumnsValue = useMemo(() => {
        return buildColumnsValue(timeSheetColumns, timeSheetHiddenColumns, config?.timeSheet?.columns, { canSort: false });
    }, [timeSheetColumns, timeSheetHiddenColumns, config]);

    const defaultFiltersValue = useMemo(() => {
        return buildFiltersValue(filters, config?.filters);
    }, [filters, config]);

    const formDefaults = useMemo<TeamTimesheetFormValues>(() => ({
        columns: defaultColumnsValue,
        filters: defaultFiltersValue,
        defaultSort: config?.defaultSort,
        defaultPageSize: String(config?.defaultPageSize || 20),
        defaultFilter: config?.defaultFilter || "",
        mandatoryNoteOnEdit: config?.mandatoryNoteOnEdit || false,
        mandatoryNoteOnCreate: config?.mandatoryNoteOnCreate || false,
        hideWeeklySummary: config?.hideWeeklySummary || false,
        hideUserProfile: config?.hideUserProfile || false,
        timeSheet: {
            columns: defaultTimesheetColumnsValue,
            hideDailySummary: config?.timeSheet?.hideDailySummary || false,
            hideClockDatePicker: config?.timeSheet?.hideClockDatePicker || false,
        },
    }), [defaultColumnsValue, defaultFiltersValue, defaultTimesheetColumnsValue, config]);

    const form = useForm<TeamTimesheetFormValues>({
        defaultValues: formDefaults,
        values: formDefaults,
        resetOptions: { keepDirtyValues: true },
    });

    const onFinish = (value: TeamTimesheetFormValues) => mutate({
        ...config,
        ...value,
        defaultPageSize: Number(value.defaultPageSize) || 20,
        timeSheet: { ...config?.timeSheet, ...value.timeSheet },
    });

    const panels: { key: string; node: ReactNode }[] = [
        { key: "user-columns", node: <Table columns={columns} fieldName="columns" /> },
        { key: "user-filters", node: <Filters config={filters} /> },
        { key: "timesheet-columns", node: <Table columns={timeSheetColumns} fieldName="timeSheet.columns" canSort={false} /> },
        { key: "settings", node: <Settings columns={columns} /> },
    ];

    return (
        <div className="tw-p-5 tw-h-full tw-bg-white tw-rounded-lg">
            <Form className="tw-h-full" form={form} onFinish={onFinish}>
                <Header name="my-team" isLoading={isPending} />
                <Menu
                    options={options}
                    option={option}
                    setOption={setOption}
                />

                <div className="tw-px-7" style={{ height: `calc(100% - 200px)` }}>
                    {panels.map(({ key, node }) => (
                        <div
                            key={key}
                            className="tw-h-full"
                            style={{ display: option === key ? undefined : "none" }}
                        >
                            {node}
                        </div>
                    ))}
                </div>
            </Form>
        </div>
    );
}

export default Team;
